import { useId, useRef, useState } from 'react';
import { Autocomplete, Box, useMediaQuery, useTheme } from '@mui/material';
import { IconChevronRight } from '@tabler/icons-react';
import EntryTypeControl from './ds/EntryTypeControl';
import { useEngineStrings } from '../user_guides/i18n/useEngineStrings';
import { entryTypeLabelKey, entryTypePromptForPayee, interpolate, isEntryType } from '../utils/entryType';

/**
 * The Ledger page's payee picker (#1943, DESIGN_SYSTEM.md 8.15): an
 * Autocomplete whose every option shows the payee's default Entry Type as a
 * badge (the full word in its type color on its tint; none when the payee has
 * no default) and ends in a chevron that opens the Entry Type flyout beside
 * that option. Used by the add row, the edit row, the card editor and the
 * quick-add Sheet, so the list, the highlight and the flyout live here and
 * the page re-renders for none of it.
 *
 * The flyout is `EntryTypeControl`, the panel the chip opens, with the same
 * content: label, segments, help icon and the caption for the highlighted
 * payee. It is preselected to what the entry would save with on that payee:
 * this entry's own choice when that payee is already the chosen one, else the
 * payee's default, else Spending. Picking a segment selects that payee (the
 * page's `onChange`, exactly as a click on the option) and sets this entry's
 * type through the prompt hook's `setValue`, so Save follows `entryTypeForSave`
 * as a chip pick does. Flyout and list close together.
 *
 * Desktop: the flyout floats right of the list, centered on its option, and
 * follows the highlight. Phones: it opens in place under the option (8.8:
 * menus never grow sideways). The list is controlled here so a blur that
 * lands in the flyout (the keyboard path) does not close it; a click on the
 * chevron or inside the flyout never blurs the field at all.
 *
 * Keyboard: with the list open and an option highlighted, ArrowRight opens
 * the flyout and focuses its checked segment; ArrowLeft closes it. Otherwise
 * both keys move the caret. Escape closes both.
 *
 * Ledger-linked payees (#1944) arrive first in `options` (see
 * `utils/payeeOptions.js`) and render under a "Ledgers" header. Their type is
 * locked to Transfer, so they carry the badge but no chevron.
 *
 * Every prop not named here goes to the Autocomplete. `renderInput` is the
 * page's: it keeps its TextField, refs, tooltip and Enter handling.
 *
 * @param {object} props
 * @param {Array} props.options            the filtered payees
 * @param {object} props.prompt            the `useEntryTypePrompt` hook for this field
 * @param {function} props.onChange        the page's payee change handler (event, payee)
 * @param {function} [props.onOpen]        the page's list-open handler
 * @param {function} props.renderInput     the page's input renderer
 */
const LEDGERS_GROUP = 'ledgers';
const PAYEES_GROUP = 'payees';

const PayeeAutocomplete = ({ options, prompt, onChange, onOpen, renderInput, ...rest }) => {
  const t = useEngineStrings();
  const theme = useTheme();
  const { palette } = theme;
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const id = useId().replace(/:/g, '');
  const [open, setOpen] = useState(false);
  // The option the flyout is on: { payee, anchorEl }.
  const [flyout, setFlyout] = useState(null);
  const [keyboard, setKeyboard] = useState(false);
  const highlightedRef = useRef(null);
  const rootRef = useRef(null);

  const optionElement = (payee) => {
    const index = options.indexOf(payee);
    const listbox = document.getElementById(`${id}-listbox`);
    return index >= 0 && listbox ? listbox.querySelector(`[data-option-index="${index}"]`) : null;
  };
  const focusInput = () => rootRef.current?.querySelector('input')?.focus();

  const openFlyout = (payee, anchorEl, viaKeyboard = false) => {
    // A ledger-linked payee is locked to Transfer: no flyout (#1944).
    if (!payee || payee.linkedLedgerId) return;
    setKeyboard(viaKeyboard);
    setFlyout({ payee, anchorEl: anchorEl || optionElement(payee) });
  };

  const handleOpen = (event) => {
    setOpen(true);
    if (onOpen) onOpen(event);
  };
  const handleClose = (event, reason) => {
    // Focus moved into the flyout (the keyboard path): the list stays.
    if (reason === 'blur' && flyout && event?.relatedTarget
      && typeof event.relatedTarget.closest === 'function' && event.relatedTarget.closest('[data-entry-type-control]')) return;
    setOpen(false);
    setFlyout(null);
  };
  const handleChange = (event, value, reason, details) => {
    setFlyout(null);
    if (onChange) onChange(event, value, reason, details);
  };
  const handleHighlightChange = (event, payee) => {
    highlightedRef.current = payee;
    // The flyout follows the highlight.
    if (flyout && payee && payee !== flyout.payee) setFlyout({ payee, anchorEl: optionElement(payee) });
  };

  // A pick from the flyout: that payee is the entry's payee and this entry's
  // type is the pick; both close.
  const pick = (type) => {
    const { payee } = flyout;
    handleChange(null, payee, 'selectOption', { option: payee });
    prompt.setValue(type);
    setOpen(false);
    focusInput();
  };
  const closeFromFlyout = () => {
    setFlyout(null);
    if (keyboard) focusInput();
  };

  const onInputKeyDown = (e) => {
    if (e.key === 'ArrowRight' && open && highlightedRef.current && !flyout) {
      e.preventDefault();
      e.stopPropagation();
      openFlyout(highlightedRef.current, null, true);
    } else if (e.key === 'ArrowLeft' && flyout) {
      e.preventDefault();
      e.stopPropagation();
      setFlyout(null);
    } else if (e.key === 'Escape' && flyout) {
      setFlyout(null);
    }
  };

  const flyoutPrompt = flyout ? entryTypePromptForPayee(flyout.payee, prompt.entry) : null;
  const control = flyoutPrompt ? (
    <EntryTypeControl
      placement={phone ? 'inline' : 'side'}
      open
      anchorEl={flyout.anchorEl}
      value={flyoutPrompt.value}
      prompt={flyoutPrompt}
      onChange={pick}
      onClose={closeFromFlyout}
      keepFocus
      autoFocus={keyboard}
      sx={phone ? { paddingTop: '4px', paddingBottom: '4px' } : undefined}
    />
  ) : null;

  const renderOption = (props, payee) => {
    const { key, ...optionProps } = props;
    const type = isEntryType(payee.defaultEntryType) ? payee.defaultEntryType : null;
    const tone = type ? palette.entryType[type] : null;
    const word = type ? t[entryTypeLabelKey(type)] : '';
    const here = phone && flyout && flyout.payee === payee;
    const locked = Boolean(payee.linkedLedgerId);
    return (
      // The option's accessible name stays the payee name; the badge and the
      // chevron are read on their own.
      <li {...optionProps} key={key} aria-label={payee.name} style={{ ...(optionProps.style || {}), flexWrap: 'wrap', gap: '8px' }}>
        <span style={{ flex: '1 1 auto', minWidth: 0 }}>{payee.name}</span>
        {type ? (
          <span
            title={word}
            data-testid="payee-option-badge"
            data-entry-type={type}
            style={{
              flexShrink: 0,
              fontSize: '11px',
              fontWeight: 500,
              letterSpacing: '.02em',
              lineHeight: '14px',
              padding: '2px 6px',
              borderRadius: '3px',
              backgroundColor: tone.bg,
              color: tone.fg,
            }}
          >
            {word}
          </span>
        ) : null}
        {locked ? (
          // Holds the chevron's width so the badges line up down the list.
          <span aria-hidden style={{ flexShrink: 0, width: 22 }} />
        ) : (
          <Box
            component="button"
            type="button"
            aria-label={interpolate(t['entryType.optionChevron'], { payee: payee.name })}
            aria-expanded={Boolean(flyout && flyout.payee === payee)}
            data-testid="payee-option-chevron"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              // Never the option's own click: that would select and close.
              e.stopPropagation();
              if (flyout && flyout.payee === payee) setFlyout(null);
              else openFlyout(payee, e.currentTarget.closest('li'));
            }}
            sx={{
              flexShrink: 0,
              width: 22,
              height: 22,
              border: 0,
              padding: 0,
              borderRadius: '4px',
              background: 'transparent',
              color: palette.text.dim,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': { color: palette.interactive, backgroundColor: palette.surface.hover },
            }}
          >
            <IconChevronRight size={14} aria-hidden />
          </Box>
        )}
        {here ? (
          // In place under the option; its clicks are its own, never the
          // option's (which would select and close).
          <Box onClick={(e) => e.stopPropagation()} sx={{ flexBasis: '100%' }}>{control}</Box>
        ) : null}
      </li>
    );
  };

  // The Ledgers group gets a header; the regular payees below it get none.
  const renderGroup = ({ key, group, children }) => (
    <li key={key}>
      {group === LEDGERS_GROUP ? (
        <Box
          component="div"
          data-testid="payee-group-ledgers"
          sx={{
            padding: '6px 16px 2px',
            fontSize: '11px',
            fontWeight: 500,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
            color: palette.text.dim,
          }}
        >
          {t['entryType.ledgersGroup']}
        </Box>
      ) : null}
      <Box component="ul" sx={{ padding: 0 }}>{children}</Box>
    </li>
  );

  return (
    <>
      <Autocomplete
        {...rest}
        groupBy={(payee) => (payee.linkedLedgerId ? LEDGERS_GROUP : PAYEES_GROUP)}
        renderGroup={renderGroup}
        id={id}
        ref={rootRef}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        options={options}
        onChange={handleChange}
        onHighlightChange={handleHighlightChange}
        renderOption={renderOption}
        renderInput={(params) => renderInput({
          ...params,
          inputProps: {
            ...params.inputProps,
            onKeyDown: (e) => {
              onInputKeyDown(e);
              if (params.inputProps.onKeyDown) params.inputProps.onKeyDown(e);
            },
          },
        })}
      />
      {!phone ? control : null}
    </>
  );
};

export default PayeeAutocomplete;
